import { Box, Tooltip, Typography } from "@mui/material";
import { memo, useCallback } from "react";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { ButtonSetting } from "./ButtonSetting/ButtonSetting";
import {
  DragDropContext,
  Droppable,
  OnDragEndResponder,
} from "react-beautiful-dnd";
import { Button, useFormContext } from "@4uhub/lib4uhub";
import {
  ICtaButton,
  useTemplateWhats,
} from "../../../../store/contexts/TemplateWhatsContext";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

const translationPath = "components.callToActionsButtons.";

export const CallToActionButtons = () => {
  const { t } = useTranslation();

  const {
    ctaButtons: ctxCtas,
    customButtons: ctxCustom,
    reorderCtaButtons,
    toggleButtonsList,
  } = useTemplateWhats();

  const { control, setValue } = useFormContext();

  const formCtas = useWatch({ control, name: "ctaButtons" }) ?? [];
  const formCustom = useWatch({ control, name: "customButtons" }) ?? [];

  const ctaButtons: ICtaButton[] =
    (ctxCtas?.length ?? 0) > 0 ? ctxCtas : formCtas;
  const customButtons = (ctxCustom?.length ?? 0) > 0 ? ctxCustom : formCustom;

  const hasTwoSections = ctaButtons.length > 0 && customButtons.length > 0;

  const changeOrder = useCallback(() => {
    toggleButtonsList("1");
  }, [toggleButtonsList]);

  const reorder = useCallback(
    (list: ICtaButton[], startIndex: number, endIndex: number) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, { ...removed, order: endIndex });

      result.forEach((item, idx) => {
        setValue(`ctaButtons.${idx}`, item, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });

      return result;
    },
    [setValue]
  );

  const onDragEnd: OnDragEndResponder = useCallback(
    (e) => {
      if (!e.destination) return;

      const itemsReordered = reorder(
        ctaButtons,
        e.source.index,
        e.destination.index
      );

      reorderCtaButtons(itemsReordered);
    },
    [ctaButtons, reorder, reorderCtaButtons]
  );

  if (!ctaButtons || ctaButtons.length === 0) return null;

  return (
    <Box
      sx={(t) => ({
        p: 2,
        border: 1,
        borderColor: t.palette.divider,
        borderRadius: 1,
      })}
    >
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
        {hasTwoSections && (
          <Tooltip
            title={t(translationPath + "change_buttons_order")}
            placement="top"
          >
            <Box>
              <Button size="small" sx={{ minWidth: 0 }} onClick={changeOrder}>
                <SwapVertIcon fontSize="small" color="primary" />
              </Button>
            </Box>
          </Tooltip>
        )}
        <Typography variant="body2" fontWeight="bold">
          {t(translationPath + "call_to_action")}
        </Typography>
      </Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="buttonsList" direction="vertical">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {ctaButtons.map((btn, index) => (
                <ButtonSetting
                  key={btn.id}
                  index={index}
                  button={btn}
                  ctaButtonsLength={ctaButtons.length}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default memo(CallToActionButtons);
